<template>
  <v-dialog v-model="internalValue" max-width="900px" persistent scrollable>
    <v-card class="rounded-xl border-light bg-white">
      <!-- Modal Header -->
      <v-card-title class="d-flex align-center justify-space-between py-3 px-5 border-light-bottom bg-white">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-file-excel</v-icon>
          <span class="text-h6 font-weight-black grey--text text--darken-4">
            Pegar Presupuesto desde Excel / Portapapeles
          </span>
        </div>
        <v-btn icon color="grey darken-2" small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Modal Body -->
      <v-card-text class="pt-4 pb-4 px-5 bg-white" style="max-height: 70vh;">
        <v-alert dense outlined type="info" class="mb-3 rounded-lg">
          <strong>Formato sugerido de columnas (separadas por tabulación desde Excel):</strong><br />
          <code>[Cód. Cliente] | [Nombre Cliente (Opcional)] | [Toneladas (TM)] | [FCL] | [FOB USD/kg] | [Observación]</code><br />
          <small>* Si el código es <code>SIN CLIENTE</code> o <code>NO</code>, se marcará automáticamente como "Sin Cliente". El FOB Total USD se calculará automáticamente: <code>TM × 1000 × FOB USD/kg</code>.</small>
        </v-alert>

        <s-textarea
          v-model="rawText"
          :rows="6"
          label="Pegar celdas copiadas de Excel aquí (Ctrl + V)"
          placeholder="Ejemplo:&#10;CLI-001	SUPERMARKETS LLC	120.50	5.0	1.850000	Programa Europa&#10;SIN CLIENTE	Reserva comercial	50.00	2.0	1.750000	Meta por asignar"
          @input="parseData"
          class="mb-3"
        />

        <div v-if="parsedRows.length > 0">
          <div class="d-flex justify-space-between align-center mb-2">
            <span class="font-weight-bold subtitle-2 text--secondary">
              Vista previa: {{ parsedRows.length }} filas detectadas
            </span>
            <span class="caption text--secondary font-weight-bold">
              Totales parseados: {{ sumTons.toFixed(2) }} TM | {{ sumFcl.toFixed(2) }} FCL | ${{ sumFob.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} FOB
            </span>
          </div>

          <v-simple-table dense class="border-rounded border-light elevation-0 detail-table mb-2">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left font-weight-bold">#</th>
                  <th class="text-left font-weight-bold">Tipo / Cód</th>
                  <th class="text-left font-weight-bold">Cliente</th>
                  <th class="text-right font-weight-bold">TM</th>
                  <th class="text-right font-weight-bold">FCL</th>
                  <th class="text-right font-weight-bold">FOB USD/kg</th>
                  <th class="text-right font-weight-bold">FOB Total USD</th>
                  <th class="text-left font-weight-bold">Obs</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, idx) in parsedRows" :key="idx">
                  <td>{{ idx + 1 }}</td>
                  <td>
                    <v-chip x-small label :color="r.EmdIsNoClient ? 'amber darken-3' : 'info'" text-color="white" class="font-weight-bold">
                      {{ r.EmdIsNoClient ? 'SIN CLIENTE' : (r.PrsCustomerID || 'SIN CÓD') }}
                    </v-chip>
                  </td>
                  <td>{{ r.EmdCustomerName }}</td>
                  <td class="text-right font-weight-bold green--text text--darken-3">{{ Number(r.EmdBudgetTons).toFixed(2) }}</td>
                  <td class="text-right font-weight-bold teal--text text--darken-3">{{ Number(r.EmdBudgetFcl).toFixed(2) }}</td>
                  <td class="text-right font-weight-bold purple--text text--darken-3">${{ Number(r.EmdPriceFobKg).toFixed(4) }}</td>
                  <td class="text-right font-weight-bold blue--text text--darken-3">${{ Number(r.EmdBudgetFobUsd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</td>
                  <td><small>{{ r.EmdObservation }}</small></td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
      </v-card-text>

      <!-- Modal Footer -->
      <v-card-actions class="px-5 pb-4 pt-3 border-light-top bg-white d-flex justify-end">
        <v-btn text class="rounded-lg px-4 text-none font-weight-bold mr-2" @click="close">
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          class="rounded-lg px-5 font-weight-bold text-none"
          :disabled="parsedRows.length === 0"
          @click="applyImport"
        >
          <v-icon left small>mdi-check</v-icon>
          Importar {{ parsedRows.length }} filas al Presupuesto
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "PasteExcelBudgetModal",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      rawText: "",
      parsedRows: [],
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    sumTons() {
      return this.parsedRows.reduce((a, b) => a + (Number(b.EmdBudgetTons) || 0), 0);
    },
    sumFcl() {
      return this.parsedRows.reduce((a, b) => a + (Number(b.EmdBudgetFcl) || 0), 0);
    },
    sumFob() {
      return this.parsedRows.reduce((a, b) => a + (Number(b.EmdBudgetFobUsd) || 0), 0);
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.rawText = "";
        this.parsedRows = [];
      }
    },
  },
  methods: {
    close() {
      this.internalValue = false;
    },
    parseData() {
      if (!this.rawText || !this.rawText.trim()) {
        this.parsedRows = [];
        return;
      }

      const lines = this.rawText.trim().split(/\r\n|\n|\r/);
      const list = [];

      lines.forEach((line) => {
        if (!line.trim()) return;

        let parts = line.split("\t");
        if (parts.length === 1 && line.includes(";")) {
          parts = line.split(";");
        }

        const cleanParts = parts.map((p) => p.trim());
        if (cleanParts.length === 0) return;

        let col1 = cleanParts[0] || "";
        let col2 = cleanParts[1] || "";
        let col3 = cleanParts[2] || "";
        let col4 = cleanParts[3] || "";
        let col5 = cleanParts[4] || "";
        let col6 = cleanParts[5] || "";
        let col7 = cleanParts[6] || "";

        let isNoClient = false;
        let customerId = "";
        let customerName = "";
        let tons = 0;
        let fcl = 0;
        let priceKg = 0;
        let fobUsd = 0;
        let observation = "";

        const cleanNum = (v) => {
          if (!v) return 0;
          let numStr = v.replace(/[$€,\s]/g, (match) => (match === "," ? "." : ""));
          if (v.includes(",") && !v.includes(".")) {
            numStr = v.replace(",", ".");
          }
          const n = parseFloat(numStr);
          return isNaN(n) ? 0 : n;
        };

        const isCol1NoClient = col1.toUpperCase().includes("SIN CLIENTE") || col1.toUpperCase() === "NO" || col1.toUpperCase() === "SIN ASIGNAR";

        if (cleanParts.length >= 7) {
          // [Col1: Cod] [Col2: Name] [Col3: TM] [Col4: FCL] [Col5: FOB USD/kg] [Col6: FOB Total] [Col7: Obs]
          if (isCol1NoClient) {
            isNoClient = true;
            customerId = null;
            customerName = col2 || "SIN CLIENTE ASIGNADO";
          } else {
            customerId = col1;
            customerName = col2;
          }
          tons = cleanNum(col3);
          fcl = cleanNum(col4);
          priceKg = cleanNum(col5);
          fobUsd = cleanNum(col6);
          observation = col7;
        } else if (cleanParts.length === 6) {
          // [Col1: Cod] [Col2: Name] [Col3: TM] [Col4: FCL] [Col5: FOB USD/kg] [Col6: Obs]
          if (isCol1NoClient) {
            isNoClient = true;
            customerId = null;
            customerName = col2 || "SIN CLIENTE ASIGNADO";
          } else {
            customerId = col1;
            customerName = col2;
          }
          tons = cleanNum(col3);
          fcl = cleanNum(col4);
          const val5 = cleanNum(col5);
          // Detectar si val5 es FOB/kg o FOB Total
          if (val5 > 50 && tons > 0) {
            fobUsd = val5;
            priceKg = Number((fobUsd / (tons * 1000)).toFixed(6));
          } else {
            priceKg = val5;
            fobUsd = Number((tons * 1000 * priceKg).toFixed(2));
          }
          observation = col6;
        } else if (cleanParts.length === 5) {
          // [Col1: Cod] [Col2: Name] [Col3: TM] [Col4: FCL] [Col5: FOB USD/kg]
          if (isCol1NoClient) {
            isNoClient = true;
            customerId = null;
            customerName = col2 || "SIN CLIENTE ASIGNADO";
          } else {
            customerId = col1;
            customerName = col2;
          }
          tons = cleanNum(col3);
          fcl = cleanNum(col4);
          const val5 = cleanNum(col5);
          if (val5 > 50 && tons > 0) {
            fobUsd = val5;
            priceKg = Number((fobUsd / (tons * 1000)).toFixed(6));
          } else {
            priceKg = val5;
            fobUsd = Number((tons * 1000 * priceKg).toFixed(2));
          }
        } else if (cleanParts.length === 4) {
          if (isCol1NoClient) {
            isNoClient = true;
            customerId = null;
            customerName = "SIN CLIENTE ASIGNADO";
          } else {
            customerId = col1;
            customerName = col1;
          }
          tons = cleanNum(col2);
          fcl = cleanNum(col3);
          const val4 = cleanNum(col4);
          if (val4 > 50 && tons > 0) {
            fobUsd = val4;
            priceKg = Number((fobUsd / (tons * 1000)).toFixed(6));
          } else {
            priceKg = val4;
            fobUsd = Number((tons * 1000 * priceKg).toFixed(2));
          }
        } else if (cleanParts.length === 3) {
          if (isCol1NoClient) {
            isNoClient = true;
            customerId = null;
            customerName = "SIN CLIENTE ASIGNADO";
          } else {
            customerId = col1;
            customerName = col1;
          }
          tons = cleanNum(col2);
          fcl = cleanNum(col3);
        }

        if (priceKg > 0 && fobUsd <= 0 && tons > 0) {
          fobUsd = Number((tons * 1000 * priceKg).toFixed(2));
        }

        if (tons > 0 || fcl > 0 || fobUsd > 0 || priceKg > 0 || customerId || isNoClient) {
          list.push({
            EmdID: 0,
            EmbID: 0,
            EmdIsNoClient: isNoClient,
            PrsCustomerID: isNoClient ? null : customerId,
            EmdCustomerName: customerName || (isNoClient ? "SIN CLIENTE ASIGNADO" : customerId),
            selectedCustomer: isNoClient ? null : { PrsCustomerID: customerId, PrsName: customerName },
            EmdBudgetTons: tons,
            EmdBudgetFcl: fcl,
            EmdPriceFobKg: priceKg,
            EmdBudgetFobUsd: fobUsd,
            EmdObservation: observation,
            SecStatus: 1,
          });
        }
      });

      this.parsedRows = list;
    },
    applyImport() {
      this.$emit("imported", this.parsedRows);
      this.close();
    },
  },
};
</script>

<style scoped>
.border-light {
  border: 1px solid #e2e8f0 !important;
}

.border-light-bottom {
  border-bottom: 1px solid #e2e8f0 !important;
}

.border-light-top {
  border-top: 1px solid #e2e8f0 !important;
}

.bg-grey-lighten-5 {
  background-color: #f8fafc !important;
}

.border-rounded {
  border-radius: 8px;
  overflow: hidden;
}

.detail-table th {
  font-size: 11px !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background-color: #f8fafc !important;
  color: #334155 !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.detail-table td {
  border-bottom: 1px solid #f1f5f9 !important;
}
</style>
